import {Component, effect, OnInit, inject} from '@angular/core';
import {DOCUMENT} from '@angular/common';
import {AuthService} from './auth/shared/services/auth.service';
import {distinctUntilChanged, filter} from 'rxjs/operators';
import {NavigationEnd, Router} from '@angular/router';
import {TrackingService} from './services/tracking.service';
import {GoogleTagManagerService} from 'angular-google-tag-manager';
import {Intercom, IntercomBootInput} from '@supy-io/ngx-intercom';
import {AccountService} from './services/account.service';
import {UtmService} from './auth/shared/services/utm.service';
import {MCActionsService} from './services/mc-base/mc-actions-service';
import {UserInfoQuery} from './common/elf-store/user-info/user-info.query';
import {UserInfoState} from './common/elf-store/user-info/user-info.type';
import {environment} from '../environments/environment';
import {AuthManager} from './common/elf-store/auth/auth.manager';

import {SaveTuningsAndAddonsChangesService} from './services/save-tunings-and-addons-changes.service';
import {AmplitudeService} from './services/amplitude.service';
import {SettingsApiService} from './services/api/settings-api.service';
import {SUBSCRIPTIONS} from './common/utils/constants';
import {AutoUnsubscribe} from './decorators/auto-unsubscribe.decorator';
import {GetUnitInfoData} from '../userway/sdk/SSOApiClient';
import {CustomGTMService} from './services/gtm.service';
import {ApiClient} from './services/api/userway-api.service';
import {AppShellComponent} from './app-shell/app-shell.component';

const WIDGET_ELEMENT_ID = 'UWAccessibilityWidget';
const QA_DATA_ACCOUNT = '6AyOfvab4R';
const PROD_DATA_ACCOUNT = '1c9dnQkJMU';

@Component({
  standalone: true,
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  imports: [AppShellComponent],
})
@AutoUnsubscribe()
export class AppComponent extends MCActionsService implements OnInit {
  auth = inject(AuthService);
  authManager = inject(AuthManager);
  router = inject(Router);
  private trackingService = inject(TrackingService);
  private document = inject<Document>(DOCUMENT);
  private gtmService = inject(GoogleTagManagerService);
  private accountService = inject(AccountService);
  intercom = inject(Intercom);
  private utmService = inject(UtmService);
  private saveTuningsService = inject(SaveTuningsAndAddonsChangesService);
  private ampliService = inject(AmplitudeService);
  private settingsApiService = inject(SettingsApiService);
  private apiClient = inject(ApiClient);

  isIntercomLoaded = false;
  private routerEvents$;

  constructor() {
    const userInfoQuery = inject(UserInfoQuery);

    super(userInfoQuery);
    this.initialized$.next(true);
    effect(() => {
      this.updateIntercomPosition(this.saveTuningsService.showSaveButtonTuningsAndAddons);
    });
  }

  mcOnLogin(userInfo: UserInfoState) {
    this.setUtm();
    this.initIntercom();
  }

  initGTM() {
    // GOOGLE TAG MANAGER CUSTOM INSTANCE CREATING
    const customGTM = this.gtmService as CustomGTMService;

    this[SUBSCRIPTIONS].add(
      this.userInfoQuery.userInfo$.subscribe(userInfo => {
        if (userInfo.ssoUserId) {
          this.settingsApiService.getSsoUnitInfo(userInfo.ssoUserId).subscribe({
            next: (response: GetUnitInfoData) => {
              const isGtmDisabled = response.payload?.settings?.isGTMDisabled;

              if (isGtmDisabled) {
                customGTM.setEnabled(false);
                console.log('Google tag manager is disabled');
              } else {
                customGTM.addGtmToDom().catch(() => {
                  console.warn('Failed to load Google Tag Manager');
                });
              }
            },
            error: () => {
              console.log('getSsoUnitInfo subscription error');
            },
          });
        }
      }),
    );
  }

  ngOnInit(): void {
    this.ampliService.init();
    this.initGTM();
    this.createUserwayWidget();

    const potentialToken = localStorage.getItem('UserWayManageAppToken');
    if (potentialToken !== null) {
      this.auth.setToken(potentialToken);
    }

    const potentialRefreshToken = localStorage.getItem('UserWayManageAppJwtRefreshToken');
    if (potentialRefreshToken !== null) {
      this.authManager.setRefreshToken(potentialRefreshToken);
    }

    this.setUtm();

    this.apiClient.initAxiosHeadersSubscriptions();
  }

  mcOnLogout() {
    this.intercom.shutdown();
    this.isIntercomLoaded = false;
  }

  initIntercom(): void {
    if (!this.isIntercomLoaded) {
      this.isIntercomLoaded = true;
      this.accountService.getUserDataIntercom().subscribe(res => {
        const intercomBootInput = {
          app_id: environment.intercomId,
          alignment: 'right',
          ...res.data,
          widget: {
            activator: '#intercom',
          },
        };
        this.intercom.boot(intercomBootInput as unknown as IntercomBootInput);
      });
    }
  }

  setUtm() {
    this.utmService.setUtmIfAny().then((uwPPCSession: string | null) => {
      if (uwPPCSession) {
        this.setupPageTracking();
      }
    });
  }

  widgetEmbedded(): boolean {
    const script = this.document.getElementById(WIDGET_ELEMENT_ID);
    return this.document.head.contains(script);
  }

  createUserwayWidget(): void {
    if (!this.widgetEmbedded()) {
      const script = this.document.createElement('script');
      script.setAttribute('id', WIDGET_ELEMENT_ID);
      script.setAttribute('data-account', environment.production ? PROD_DATA_ACCOUNT : QA_DATA_ACCOUNT);
      script.setAttribute('src', environment.widgetUrl);

      if (this?.document?.head?.append) {
        this.document.head.append(script);
      }
    }
  }

  setupPageTracking(): void {
    if (!this.routerEvents$) {
      this.routerEvents$ = this.router.events
        .pipe(
          filter((event: NavigationEnd) => event instanceof NavigationEnd),
          distinctUntilChanged((previousEvent, currentEvent) => previousEvent.url === currentEvent.url),
        )
        .subscribe(event => {
          this.trackingService.trackEvent({
            page: event.url,
            event: 'pload',
          });
        });
    }
  }

  private updateIntercomPosition(moveAbove: boolean) {
    if (this.isIntercomLoaded) {
      this.intercom.update({
        //20 - default intercom padding; 70 - padding for intercom button to be above save buttons
        vertical_padding: moveAbove ? 70 : 20,
      });
    }
  }
}
